/**
 * Nuxt 내장 컴포넌트 shim (NuxtLink / ClientOnly / NuxtPage 등)
 * main.js에서 전역 등록한다.
 */
import { defineComponent, h, ref, computed, onMounted } from "vue";
import { RouterLink, RouterView } from "vue-router";

const EXTERNAL_RE = /^(https?:)?\/\/|^mailto:|^tel:|^#/;

export const NuxtLink = defineComponent({
  name: "NuxtLink",
  inheritAttrs: false,
  props: {
    to: { type: [String, Object], default: "" },
    href: { type: [String, Object], default: "" },
    replace: { type: Boolean, default: false },
    target: { type: String, default: null },
    rel: { type: String, default: null },
    external: { type: Boolean, default: false },
    activeClass: { type: String, default: null },
    exactActiveClass: { type: String, default: null },
    // Nuxt 전용 옵션 — SPA에서는 무시
    prefetch: { type: Boolean, default: undefined },
    noPrefetch: { type: Boolean, default: undefined },
    prefetchedClass: { type: String, default: null },
  },
  setup(props, { slots, attrs }) {
    const dest = computed(() => props.to || props.href || "");
    const isExternal = computed(
      () => props.external || (typeof dest.value === "string" && EXTERNAL_RE.test(dest.value)),
    );

    return () => {
      if (isExternal.value) {
        return h(
          "a",
          { ...attrs, href: dest.value, target: props.target, rel: props.rel },
          slots.default?.(),
        );
      }
      return h(
        RouterLink,
        {
          ...attrs,
          to: dest.value,
          replace: props.replace,
          target: props.target,
          ...(props.activeClass ? { activeClass: props.activeClass } : {}),
          ...(props.exactActiveClass ? { exactActiveClass: props.exactActiveClass } : {}),
        },
        slots.default,
      );
    };
  },
});

export const ClientOnly = defineComponent({
  name: "ClientOnly",
  setup(_, { slots }) {
    const mounted = ref(false);
    onMounted(() => {
      mounted.value = true;
    });
    return () => (mounted.value ? slots.default?.() : slots.fallback?.() || slots.placeholder?.());
  },
});

const noop = (name) => defineComponent({ name, setup: () => () => null });

export const NuxtRouteAnnouncer = noop("NuxtRouteAnnouncer");
export const NuxtLoadingIndicator = noop("NuxtLoadingIndicator");
// vite-plugin-pwa는 manifest link를 index.html에 직접 주입하므로 no-op
export const NuxtPwaManifest = noop("NuxtPwaManifest");

export const NuxtPage = defineComponent({
  name: "NuxtPage",
  setup: () => () => h(RouterView),
});

/**
 * Nuxt 기본 404 페이지(nuxt/dist/app/components/error-404.vue)와 같은 내용.
 * 매칭되는 라우트가 없을 때 Nuxt 는 이 화면을 띄운다.
 *
 * vite/nuxt/ 는 tailwind content 범위 밖이라 인라인 스타일로 작성한다.
 * 프로젝트 디자인에 맞춘 화면을 쓰고 싶으면 이 컴포넌트만 교체하면 된다
 * (Nuxt 쪽에서는 error.vue 를 추가하는 것과 같다).
 */
export const NuxtErrorPage = defineComponent({
  name: "NuxtErrorPage",
  setup() {
    return () =>
      h(
        "div",
        {
          style:
            "display:grid;place-content:center;min-height:100vh;text-align:center;" +
            "background:#fff;color:#000;font-family:ui-sans-serif,system-ui,sans-serif;",
        },
        [
          h("h1", { style: "font-size:6rem;font-weight:500;margin:0 0 2rem;line-height:1;" }, "404"),
          h(
            "p",
            { style: "font-size:1.25rem;font-weight:300;line-height:1.25;margin:0 0 4rem;padding:0 2rem;" },
            "요청하신 페이지를 찾을 수 없습니다.",
          ),
          h(
            NuxtLink,
            {
              to: "/",
              style:
                "display:inline-block;padding:.5rem 1rem;border:1px solid #e2e2e2;" +
                "border-radius:.5rem;cursor:pointer;text-decoration:none;color:inherit;",
            },
            () => "홈으로 돌아가기",
          ),
        ],
      );
  },
});

export function registerNuxtComponents(app) {
  app.component("NuxtLink", NuxtLink);
  app.component("ClientOnly", ClientOnly);
  app.component("NuxtPage", NuxtPage);
  app.component("NuxtRouteAnnouncer", NuxtRouteAnnouncer);
  app.component("NuxtLoadingIndicator", NuxtLoadingIndicator);
  app.component("NuxtPwaManifest", NuxtPwaManifest);
}
